import React, { useMemo } from 'react';
import type { SiteRagReport } from '../../../types/siteRag';
import type { UserAuditResult } from '../../../types/userAudit';
import type { CoherenceFinding } from '../../../lib/audit/sectionCoherenceAuditor';

function reportViewHref(reportId: string, sectionId: string): string {
  return `#/report/${reportId}?section=${encodeURIComponent(sectionId)}`;
}

function severityClass(severity: CoherenceFinding['severity']): string {
  switch (severity) {
    case 'block': return 'text-red-600';
    case 'warn': return 'text-amber-600';
    default: return 'text-text-secondary';
  }
}

export function SectionCoherencePanel({
  reports,
  auditResult,
}: {
  reports: SiteRagReport[];
  auditResult?: UserAuditResult | null;
}) {
  const rows = useMemo(() => {
    const out: Array<{
      reportId: string;
      reportType?: string;
      finding: CoherenceFinding;
    }> = [];

    if (auditResult?.sectionCoherenceByReport) {
      for (const report of reports) {
        const bucket = auditResult.sectionCoherenceByReport[report.id];
        for (const f of bucket?.findings ?? []) {
          out.push({
            reportId: report.id,
            reportType: bucket?.reportType ?? report.reportType,
            finding: f,
          });
        }
      }
    }

    return out;
  }, [auditResult?.sectionCoherenceByReport, reports]);

  if (rows.length === 0) return null;

  return (
    <section className="mt-4" data-testid="section-coherence-panel">
      <h3 className="text-sm font-bold uppercase tracking-widest text-text-secondary mb-2">
        Cross-Section Coherence (R-9)
      </h3>
      <p className="text-sm text-text-secondary mb-3">
        Gemini Flash judge flags contradicting claims across section pairs.
        Opt-in via <code>VITE_AUDIT_ENABLE_SECTION_COHERENCE</code>.
      </p>
      <ul className="space-y-3 text-sm">
        {rows.map(({ reportId, reportType, finding }) => (
          <li
            key={`${reportId}-${finding.pairId}`}
            className="rounded border border-border p-3"
            data-testid={`section-coherence-finding-${finding.pairId}`}
          >
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="font-medium">{reportType ?? 'report'}</span>
              <span className={`text-xs uppercase font-semibold ${severityClass(finding.severity)}`}>
                {finding.severity}
              </span>
              <span className="text-xs text-text-secondary">{finding.topic}</span>
            </div>
            <p className="font-medium">{finding.message}</p>
            {finding.rationale && (
              <p className="text-xs text-text-secondary mt-1 italic">{finding.rationale}</p>
            )}
            {(finding.sectionAClaim || finding.sectionBClaim) && (
              <dl className="mt-2 text-xs space-y-1">
                {finding.sectionAClaim && (
                  <div>
                    <dt className="text-text-secondary">{finding.sectionA}</dt>
                    <dd>&ldquo;{finding.sectionAClaim}&rdquo;</dd>
                  </div>
                )}
                {finding.sectionBClaim && (
                  <div>
                    <dt className="text-text-secondary">{finding.sectionB}</dt>
                    <dd>&ldquo;{finding.sectionBClaim}&rdquo;</dd>
                  </div>
                )}
              </dl>
            )}
            {finding.suggestedHarmonization && (
              <p className="text-xs mt-2 p-2 bg-surface-1/60 rounded border border-border/50">
                Suggested harmonization: {finding.suggestedHarmonization}
              </p>
            )}
            <div className="flex flex-wrap gap-3 mt-2">
              <a
                href={reportViewHref(reportId, finding.sectionA)}
                className="text-primary text-xs font-semibold hover:underline"
                data-testid={`section-coherence-link-${finding.sectionA}`}
              >
                Jump to {finding.sectionA}
              </a>
              <a
                href={reportViewHref(reportId, finding.sectionB)}
                className="text-primary text-xs font-semibold hover:underline"
                data-testid={`section-coherence-link-${finding.sectionB}`}
              >
                Jump to {finding.sectionB}
              </a>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
